import { useState } from "react";
import { useLive, patch, formatPhone, STAGE_LABEL } from "../lib/api";
import type { FollowupData, FollowupRule, Stage } from "../lib/api";
import { Card, Pill, Button, Empty, Stat, Carregando } from "../lib/ui";

/**
 * Cadência de follow-up e o que está na fila.
 *
 * A cadência é dado, não código: quem opera vai querer afrouxar o primeiro
 * toque ou cortar a última tentativa depois de ver o resultado, e isso não
 * pode exigir deploy.
 */
export function Followup() {
  const { data, reload } = useLive<FollowupData>("/followups", ["leads", "messages"]);

  if (!data) return <Carregando />;
  const { fila, stats, regras } = data;

  const taxa =
    stats.enviados_7d > 0
      ? Math.round((stats.responderam_7d / stats.enviados_7d) * 100)
      : null;

  const porEtapa = new Map<string, FollowupRule[]>();
  for (const r of regras) {
    const lista = porEtapa.get(r.stage) ?? [];
    lista.push(r);
    porEtapa.set(r.stage, lista);
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Na fila" value={stats.agendados} hint="toques agendados" />
        <Stat label="Enviados 7d" value={stats.enviados_7d} />
        <Stat
          label="Taxa de resposta"
          value={taxa != null ? `${taxa}%` : "—"}
          hint="responderam em até 48h"
          tone={taxa != null && taxa < 10 ? "warm" : undefined}
        />
        <Stat
          label="Cancelados 7d"
          value={stats.cancelados_7d}
          hint="o lead voltou antes"
        />
      </div>

      {taxa != null && taxa < 10 && stats.enviados_7d >= 20 && (
        <Card stripe="warm" className="p-3">
          <p className="text-sm text-ink-2">
            Menos de 10% de resposta em {stats.enviados_7d} toques. Follow-up que
            ninguém responde não é persistência, é ruído — e ruído no WhatsApp
            custa o número. Vale afrouxar a cadência ou revisar os ângulos.
          </p>
        </Card>
      )}

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-ink">
          Próximos toques
        </h2>
        {fila.length === 0 ? (
          <Empty>Nada agendado. Ninguém está em silêncio no momento.</Empty>
        ) : (
          <div className="space-y-1.5">
            {fila.map((f) => (
              <Card key={f.id} className="px-3 py-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <span className="text-ink">{f.lead_name ?? "Sem nome"}</span>
                  <span className="text-xs tabular-nums text-ink-3">
                    {formatPhone(f.lead_phone)}
                  </span>
                  <Pill>{STAGE_LABEL[f.stage_at_schedule as Stage] ?? f.stage_at_schedule}</Pill>
                  <span className="text-xs text-ink-3">toque {f.attempt}</span>
                  <span className="ml-auto text-xs tabular-nums tabular-nums text-accent">
                    {emQuanto(f.minutes_until)}
                  </span>
                </div>
                <p className="text-xs text-ink-3 mt-1 leading-snug">{f.hook}</p>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">
            Cadência
          </h2>
          <p className="text-xs text-ink-3 mt-1 max-w-prose">
            O intervalo conta a partir da última mensagem enviada. Trocar de
            etapa reinicia a sequência — um lead que avançou merece toques
            novos. Quando a etapa acaba, o lead dorme e só volta por evento.
          </p>
        </div>

        {[...porEtapa.entries()].map(([stage, rules]) => (
          <Card key={stage} className="p-4">
            <h3 className="text-sm font-medium mb-3">
              {STAGE_LABEL[stage as Stage] ?? stage}
            </h3>
            <div className="space-y-2">
              {rules.map((r) => (
                <RegraLinha key={r.id} regra={r} onSaved={reload} />
              ))}
            </div>
          </Card>
        ))}
      </section>
    </div>
  );
}

function RegraLinha({
  regra,
  onSaved,
}: {
  regra: FollowupRule;
  onSaved: () => void;
}) {
  const [delay, setDelay] = useState(String(regra.delay_minutes));
  const [hook, setHook] = useState(regra.hook);
  const [sujo, setSujo] = useState(false);

  async function salvar() {
    await patch(`/followups/rules/${regra.id}`, {
      delayMinutes: Number(delay),
      hook,
    });
    setSujo(false);
    onSaved();
  }

  return (
    <div className={`space-y-1.5 ${regra.active ? "" : "opacity-50"}`}>
      <div className="flex items-center gap-2">
        <span className="text-xs tabular-nums text-ink-3 w-14 shrink-0">
          toque {regra.attempt}
        </span>
        <input
          value={delay}
          onChange={(e) => {
            setDelay(e.target.value.replace(/\D/g, ""));
            setSujo(true);
          }}
          className="w-20 bg-surface-2 border border-rule-2 rounded-lg px-2 py-1 text-sm
            text-ink tabular-nums focus:border-accent outline-none"
        />
        <span className="text-xs text-ink-3 w-28">
          min ({humano(Number(delay))})
        </span>
        <Button
          variant={regra.active ? "ghost" : "default"}
          onClick={async () => {
            await patch(`/followups/rules/${regra.id}`, { active: !regra.active });
            onSaved();
          }}
          className="ml-auto"
        >
          {regra.active ? "desligar" : "ligar"}
        </Button>
      </div>
      <div className="flex gap-2 pl-16">
        <textarea
          value={hook}
          onChange={(e) => {
            setHook(e.target.value);
            setSujo(true);
          }}
          rows={2}
          className="flex-1 bg-surface-2 border border-rule-2 rounded-lg px-2 py-1.5 text-xs
            text-ink leading-snug resize-none focus:border-accent outline-none"
        />
        {sujo && (
          <Button variant="primary" onClick={salvar}>
            Salvar
          </Button>
        )}
      </div>
    </div>
  );
}

function humano(min: number): string {
  if (!Number.isFinite(min) || min <= 0) return "—";
  if (min < 60) return `${min}min`;
  if (min < 1440) return `${Math.round(min / 60)}h`;
  return `${Math.round(min / 1440)}d`;
}

function emQuanto(min: number): string {
  if (min <= 0) return "sai agora";
  return `em ${humano(min)}`;
}
